import { useEffect, useRef, useState } from 'react';
import { loadServerThreadMeta, seedServerThread, subscribeThreadMeta } from '@/server-threads';
import { newThreadId } from '@/thread-types';
import {
  chatHash,
  chatIdFromHash,
  createThreadNavigation,
  isChatHash,
  writeWorkspaceHash,
} from './chat-links';

export type ChatNavigationStatus = 'ready' | 'loading' | 'error';

/** The URL selects a conversation; the server still decides who may read it.
 * Empty chats are not history. Their UUID enters the URL only after the first
 * submitted message has been saved, before the model request is dispatched. */
export function useChatNavigation(options: {
  enabled: boolean;
  ready: boolean;
  userId?: string;
  threadId: string;
  selectThread: (id: string) => void;
  isCurrentOwner: () => boolean;
}) {
  const current = useRef(options);
  current.current = options;
  const navigation = useRef(createThreadNavigation()).current;
  const selectedTarget = useRef<string | null | undefined>(undefined);
  const loadedTarget = useRef<string | null>(null);
  const [linkId, setLinkId] = useState<string | null>(null);
  const [status, setStatus] = useState<ChatNavigationStatus>('ready');
  // Collaboration v1: who can read the open chat and how this person may use
  // it (server-threads.ts ServerThreadMeta), re-read after a share.
  const [, setMetaVersion] = useState(0);
  useEffect(() => subscribeThreadMeta(() => setMetaVersion((value) => value + 1)), []);

  /** Returns the conversation it started, so a caller can write into that
   * thread before the selection has travelled back through the App. */
  function fresh() {
    navigation.cancel();
    selectedTarget.current = null;
    loadedTarget.current = null;
    setLinkId(null);
    setStatus('ready');
    const id = newThreadId();
    current.current.selectThread(id);
    return id;
  }

  async function resolve() {
    const state = current.current;
    if (!state.enabled || !state.ready || !state.userId || !state.isCurrentOwner()) return;
    if (!isChatHash(location.hash)) {
      navigation.cancel();
      // A page change must not allow a slow chat read to take over later.
      selectedTarget.current = loadedTarget.current;
      setStatus('ready');
      return;
    }
    const id = chatIdFromHash(location.hash);
    if (!id) {
      // Malformed links must not show an unrelated, previously open chat.
      if (new URLSearchParams(location.hash.split('?')[1]).has('thread')) {
        navigation.cancel();
        selectedTarget.current = undefined;
        setStatus('error');
      } else if (selectedTarget.current !== null) fresh();
      return;
    }
    if (selectedTarget.current === id) return;
    selectedTarget.current = id;
    const latest = navigation.begin();
    const owns = () => latest() && state.isCurrentOwner();
    setStatus('loading');
    try {
      await seedServerThread(id, owns);
      if (!owns()) return;
      loadedTarget.current = id;
      setLinkId(id);
      state.selectThread(id);
      setStatus('ready');
    } catch {
      if (owns()) setStatus('error');
    }
  }

  useEffect(() => {
    selectedTarget.current = undefined;
    loadedTarget.current = null;
    setLinkId(null);
    setStatus('ready');
    void resolve();
    // Workspace guards may restore an edited Settings URL during this event.
    // Resolve after every listener has run, regardless of mount/listener order,
    // so a rejected navigation never starts a conversation read.
    let active = true;
    const changed = () =>
      queueMicrotask(() => {
        if (active) void resolve();
      });
    window.addEventListener('hashchange', changed);
    return () => {
      active = false;
      navigation.cancel();
      window.removeEventListener('hashchange', changed);
    };
    // `current.current` already carries the latest callbacks; keying on their
    // identity re-hydrates and resets link state on every render that recreates them.
  }, [options.enabled, options.ready, options.userId]);

  return {
    linkId,
    status,
    /** The open chat's collaboration meta; null for an ordinary owned chat. */
    meta: loadServerThreadMeta(options.threadId),
    /** Re-read the open chat (after its sharing changed). */
    refreshMeta() {
      const state = current.current;
      if (!state.enabled || !state.userId || !state.isCurrentOwner()) return;
      void seedServerThread(state.threadId, state.isCurrentOwner).catch(() => {});
    },
    fresh() {
      const id = fresh();
      writeWorkspaceHash(chatHash());
      return id;
    },
    open(id: string) {
      if (chatIdFromHash(location.hash) === id) {
        if (status === 'error') {
          selectedTarget.current = undefined;
          void resolve();
        }
      } else writeWorkspaceHash(chatHash(id));
    },
    retry() {
      selectedTarget.current = undefined;
      void resolve();
    },
    saved(id: string) {
      const state = current.current;
      if (!state.enabled || !state.isCurrentOwner() || state.threadId !== id) return;
      loadedTarget.current = id;
      setLinkId(id);
      if (!isChatHash(location.hash)) {
        selectedTarget.current = id;
        return;
      }
      const requested = chatIdFromHash(location.hash);
      if (requested && requested !== id) return;
      selectedTarget.current = id;
      writeWorkspaceHash(chatHash(id, location.hash), 'replace');
    },
  };
}
